import {
	state,
	mutations,
	actions,
	getters
} from "@/store/build-module/common";
import changelogActions from "@/store/build-module/changelog-actions";
import { getScope } from "@/js/auth/auth-config";
import { manageError } from "@/store/helpers/store-error";
import baseAPIBuilder from "@/js/api/builder/api-builder";
import { extendContact, toApiContact } from "@/store/modules/contact";

import { UPDATE_RESOURCE, EMIT_EDITOR_USER_LIST } from "@/env";

const basePath = "/24.08/contacts";
const module = "contacts";

const meta = {
	module,
	basePath,
	mapping: {
		created_at: "createdAt",
		end_time: "endTime",
		entity_individual: "entityIndividual",
		entity_system: "entitySystem",
		external_ref: "externalRef",
		mission_profile_id: "missionProfile",
		reservation_id: "reservationId",
		setup_duration: "setupDuration",
		signed_at: "signedAt",
		spacecraft_id: "spacecraft",
		start_time: "startTime",
		station_id: "station",
		system_id: "system",
		teardown_duration: "teardownDuration",
		tenant_id: "tenant",
		updated_after: "updatedAfter",
		updated_at: "updatedAt"
	},
	dateFields: [
		"start_time",
		"end_time",
		"created_at",
		"signed_at",
		"updated_at",
		"updated_after"
	],
	parser: (data) => data.contact || data.data || data,
	inputWrapper: (input) => ({ data: input }),
	scope: await getScope("kogs")
};
const builder = new baseAPIBuilder(meta);
const api = builder.build(["postList", "add", "find", "update", "remove"]);

const baseActions = actions(api);

export default {
	namespaced: true,
	state: {
		...state(module)
	},
	getters: {
		...getters(module)
	},
	mutations: {
		...mutations(module),
		update: () => {
			/* empty placeholder for distributing updates to contact-schedule and contact */
		},
		updateState: () => {
			/* empty placeholder for distributing updates to contact-schedule and contact */
		},
		updateTicket: () => {
			/* empty placeholder for distributing updates to contact-schedule and contact */
		}
	},
	actions: {
		...baseActions,
		...changelogActions({ connection: api.connection, objectType: module }),

		ensure: async ({ dispatch }) => {
			api.connection.socket.on(UPDATE_RESOURCE, ({ id }) =>
				dispatch("fetchOne", id)
			);
			api.connection.socket.on(EMIT_EDITOR_USER_LIST, (userList) =>
				dispatch("setEditorUserList", userList)
			);

			// ensure all dependencies
			await dispatch("reservations/initiate", null, { root: true });

			await Promise.all(
				[
					"spacecrafts",
					"systems",
					"missionProfiles",
					"stations",
					"tenants",
					"organizations"
				].map((moduleName) =>
					dispatch(`${moduleName}/ensure`, {}, { root: true })
				)
			);
		},

		fetch: async ({ dispatch }, filter) => {
			const contacts = await dispatch("fetchRaw", filter);
			// Schedule timeouts for active contacts and set the new state if they are active.
			const activeStates = await dispatch(
				"frontendStates/scheduleContacts",
				contacts,
				{ root: true }
			);
			activeStates.forEach(
				({ id, newState }) =>
					(contacts.find((c) => c.id === id).newState = newState)
			);
			return contacts;
		},

		fetchRaw(scope, filter) {
			return api.postList(filter);
		},

		fetchOne: async ({ dispatch, commit }, id) => {
			const contact = await api.find(id);
			const activeStates = await dispatch(
				"frontendStates/scheduleContacts",
				[contact],
				{ root: true }
			);
			if (activeStates.length) {
				contact.newState = activeStates[0].newState;
			}
			commit("update", contact);
			return contact;
		},

		create({ commit, dispatch }, { contact, isInternal }) {
			return api
				.add(toApiContact(contact))
				.then(async (newContact) => {
					if (isInternal) {
						return newContact;
					}

					await dispatch("reservations/add", extendContact(newContact), {
						root: true
					});
					return await api.find(newContact.id);
				})
				.then((newContact) => {
					commit("update", newContact);
					return newContact;
				})
				.catch(manageError);
		},
		update(context, contact) {
			return baseActions.update(context, toApiContact(contact));
		},
		cancel({ dispatch, context }, { id, onBehalfOf, reason }) {
			if (!onBehalfOf && !reason) {
				return baseActions.remove(context, id);
			}
			// Cancel the contact with a reason and on behalf of another user
			const queryParams = new URLSearchParams();
			if (onBehalfOf && onBehalfOf !== "") {
				queryParams.append("on_behalf_of", onBehalfOf);
			}
			if (reason && reason !== "") {
				queryParams.append("reason", reason);
			}
			return builder.connection.axios
				.delete(`/${id}?${queryParams.toString()}`)
				.then(() => {
					dispatch("fetchOne", id);
					return api.find(id);
				})
				.then((contact) => {
					api.connection.socket.emit(UPDATE_RESOURCE, contact);
					return contact;
				})
				.catch(manageError);
		}
	}
};

const connection = builder.connection;
export { connection, api };
